/**
 * Datos estáticos del módulo radio.
 *
 * Catálogo de covers disponibles, playlist por defecto y posiciones por
 * defecto de los hotspots del player.
 *
 * Los offsets específicos de cada cover (`spineOffsetXPercent`,
 * `spineAlignBottom`) se introducirán en Fase 4c con valores ajustados
 * por cover. De momento se mantienen sin override (comportamiento legacy).
 */

import type { CoverMeta, Playlist, HotspotMap } from './types.js';

/**
 * Catálogo de carátulas disponibles. El orden importa: es el que se muestra
 * en el modal de "+ NUEVA PLAYLIST" para elegir.
 *
 * Nota: las rutas son relativas (sin barra inicial). El consumidor las
 * compone con `/covers/${file}` cuando construye el src del <img>.
 */
export const AVAILABLE_COVERS: readonly CoverMeta[] = [
  {
    id: '51sqnplaylist',
    name: '51 Escuadrón Playlist',
    file: 'covers/51sqnplaylist.png',
    front: 'covers/frontal_51sqnplaylist.png',
    textBaked: true,
  },
  {
    id: 'cover_2',
    name: 'Pedal y Freno',
    file: 'covers/cover_2.png',
    front: 'covers/frontal_cover_2.png',
    textColor: '#ffd83a', // amarillo
    spineOffsetXPercent: 9,
  },
  {
    id: 'cover_3',
    name: 'Synthwave Studio',
    file: 'covers/cover_3.png',
    front: 'covers/frontal_cover_3.png',
    textColor: '#5ad8ff', // azul neón
    spineOffsetXPercent: 7.5,
  },
  {
    id: 'cover_4',
    name: 'Cyber Knight',
    file: 'covers/cover_4.png',
    front: 'covers/frontal_cover_4.png',
    textColor: '#5ad8ff', // azul neón
  },
  {
    id: 'cover_5',
    name: 'Side A',
    file: 'covers/cover_5.png',
    front: 'covers/frontal_cover_5.png',
    textColor: '#d86810', // naranja oscuro
    spineOffsetXPercent: 7.5,
  },
  {
    id: 'cover_6',
    name: 'Cover 6',
    file: 'covers/cover_6.png',
    front: 'covers/frontal_cover_6.png',
    textColor: '#fff898', // amarillo claro neón
    spineOffsetXPercent: 7.5,
  },
  {
    id: 'cover_7',
    name: 'Cover 7',
    file: 'covers/cover_7.png',
    front: 'covers/frontal_cover_7.png',
    textColor: '#c8c8c8', // gris metálico
    spineOffsetXPercent: 15.5,
    spineRotationDeg: 89.5,
  },
  {
    id: 'cover_8',
    name: 'Cover 8',
    file: 'covers/cover_8.png',
    front: 'covers/frontal_cover_8.png',
    textColor: '#ff3a3a', // rojo brillante
    spineOffsetXPercent: 15.5,
    spineRotationDeg: 89.5,
  },
];

/**
 * Playlist default — "51 ESCUADRÓN PLAYLIST". Única de fábrica, no se puede
 * borrar (`locked: true`). Empieza vacía; la rellena `loadServerMusic()`
 * con las tracks del manifiesto `/music/index.json` al arrancar la radio.
 */
export const DEFAULT_PLAYLIST: Playlist = {
  id: '51esc_default',
  name: '51 ESCUADRÓN PLAYLIST',
  coverId: '51sqnplaylist',
  tracks: [],
  locked: true,
};

/** Clave de localStorage para las playlists guardadas. */
export const STORAGE_KEY = '51esc.radio.playlists';

/** Clave de localStorage para los hotspots del player. */
export const HOTSPOTS_KEY = '51esc.radio.hotspots';

/**
 * Posiciones por defecto de los hotspots, en % sobre la imagen del player
 * (`radio_player.png`, 1498×896). Calibradas con el editor (tecla E) y
 * horneadas aquí desde localStorage — así cualquier visitante (incluida la
 * web de producción, que arranca con localStorage vacío) ve los sprites
 * bien colocados. El usuario puede reajustarlas con el editor y se
 * persisten en localStorage; al pulsar RESET vuelven a estos valores.
 */
export const DEFAULT_HOTSPOTS: HotspotMap = {
  rec: { left: 34.66, top: 87.42, width: 5.35, height: 9.82 },
  play: { left: 39.85, top: 87.69, width: 5.35, height: 9.27 },
  rew: { left: 44.98, top: 87.69, width: 5.15, height: 9.82 },
  ff: { left: 49.9, top: 87.69, width: 5.15, height: 9.54 },
  stop: { left: 54.83, top: 87.69, width: 5.45, height: 9.54 },
  pause: { left: 59.95, top: 87.69, width: 4.96, height: 9.27 },
  led: { left: 3.31, top: 37.1, width: 2.39, height: 3.52 },
  led_play: { left: 41.33, top: 82.18, width: 2.09, height: 1.5 },
  reel_l: { left: 43.36, top: 60.9, width: 3.0, height: 5.1 },
  reel_r: { left: 53.92, top: 60.63, width: 3.0, height: 5.1 },
  knob: { left: 89.93, top: 31.52, width: 6.0, height: 10.0 },
};
